import { createFileRoute, useNavigate } from '@tanstack/react-router'
import type { PaginationState } from '@tanstack/react-table'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { AccountState, type UserSortingState, userSortingColumns } from 'types'
import z from 'zod'

import useErrorMessage from '@hooks/use-error-message'

import {
  useMutationUserSetActive,
  useMutationUserSetTwoFactor,
} from '@api/user/user.mutations'
import { queryUserList } from '@api/user/user.queries'

import AccountStateTabs from '@/components/filters/account-state-tabs'
import SearchBar from '@/components/search-bar'
import UsersTable from '@/components/tables/users/users-table'
import ensureUserRoleOnRoute from '@/lib/ensure-user-role-on-route'
import { paginationSchema, sortingSchema } from '@/lib/table'

import SectionHeader from '../-components/section-layout/section-header'
import SectionLayout from '../-components/section-layout/section-layout'
import UserArchiveDialog from './-components/user-archive-dialog'
import UserCreateDialog from './-components/user-create-dialog'
import UserPatchDialog from './-components/user-patch-dialog'
import UserRecoveryDialog from './-components/user-recovery-dialog'

const searchSchema = z.object({
  pagination: paginationSchema,
  sorting: sortingSchema(userSortingColumns),
  filters: z
    .object({
      search: z.string().optional(),
      state: z.enum(AccountState).default(AccountState.Active),
    })
    .default({ state: AccountState.Active }),
})

export const Route = createFileRoute('/app/users')({
  component: RouteComponent,
  validateSearch: searchSchema,
  // Route requires being an admin
  beforeLoad: async ({ context }) => {
    // Route requires to be an admin
    await ensureUserRoleOnRoute(context.queryClient)
  },
  loaderDeps: ({ search }) => search,
  loader: ({
    context: { queryClient },
    deps: { pagination, filters, sorting },
  }) => {
    queryClient.prefetchQuery(
      queryUserList({
        ...pagination,
        pageIndex: pagination.pageIndex + 1,
        filters,
        sorting,
      }),
    )
  },
})

function RouteComponent() {
  const [archiveUserUuid, setArchiveUserUuid] = useState<string | null>(null)
  const [patchUserUuid, setPatchUserUuid] = useState<string | null>(null)
  const [recoveryUserUuid, setRecoveryUserUuid] = useState<string | null>(null)
  const { filters, pagination, sorting } = Route.useSearch()
  const { t } = useTranslation()
  const navigate = useNavigate({ from: Route.fullPath })

  const { mutate: mutateSetActive } = useMutationUserSetActive()
  const { mutate: mutateSetTwoFactor } = useMutationUserSetTwoFactor()
  const errorMessage = useErrorMessage()

  const handleSetActive = useCallback(
    (uuid: string, active: boolean) => {
      mutateSetActive(
        { uuid, active },
        {
          onError: (error) => {
            const message = errorMessage({ error })
            toast.error(message)
          },
        },
      )
    },
    [mutateSetActive, errorMessage],
  )

  const handleToggleTwoFactor = useCallback(
    (uuid: string, enabled: boolean) => {
      mutateSetTwoFactor(
        { uuid, enabled },
        {
          onError: (error) => {
            const message = errorMessage({ error })
            toast.error(message)
          },
        },
      )
    },
    [mutateSetTwoFactor, errorMessage],
  )

  const handlePatchOpenChange = (open: boolean) =>
    setPatchUserUuid(open ? patchUserUuid : null)

  const handleArchiveOpenChange = (open: boolean) =>
    setArchiveUserUuid(open ? archiveUserUuid : null)

  const handleRecoveryOpenChange = (open: boolean) =>
    setRecoveryUserUuid(open ? recoveryUserUuid : null)

  const setAccountState = (state: AccountState) =>
    navigate({
      to: '.',
      search: (previous) => ({
        ...previous,
        pagination: { ...previous.pagination, pageIndex: 0 },
        filters: { ...previous.filters, state },
      }),
    })

  const setPagination = (
    updater: (pagination: PaginationState) => PaginationState,
  ) => {
    const nextPagination = updater(pagination)
    navigate({
      to: '.',
      search: (previous) => ({ ...previous, pagination: nextPagination }),
    })
  }

  const setSorting = (
    updater: (sorting?: UserSortingState) => UserSortingState,
  ) => {
    const nextSorting = updater(sorting)
    navigate({
      to: '.',
      search: (previous) => ({ ...previous, sorting: nextSorting }),
    })
  }

  return (
    <SectionLayout title={t('users.title')} className="overflow-hidden">
      <div className="flex h-full grow flex-col divide-y overflow-hidden">
        <SectionHeader title={t('users.title')}>
          <div className="flex items-center gap-2">
            <SearchBar
              tabIndex={1}
              className="max-w-62.75"
              text={filters.search}
              setText={(search) =>
                navigate({
                  to: '.',
                  search: (previous) => ({
                    ...previous,
                    filters: { ...previous.filters, search },
                  }),
                })
              }
            />
            <UserCreateDialog onRecoverConflict={setRecoveryUserUuid} />
          </div>
        </SectionHeader>
        <div className="flex h-full flex-col gap-2 overflow-hidden p-2">
          <AccountStateTabs
            value={filters.state}
            onValueChange={setAccountState}
          />
          <UsersTable
            filters={filters}
            pagination={pagination}
            setPagination={setPagination}
            sorting={sorting}
            setSorting={setSorting}
            onSetActive={handleSetActive}
            onArchive={setArchiveUserUuid}
            onPatch={setPatchUserUuid}
            onRecover={setRecoveryUserUuid}
            onToggleTwoFactor={handleToggleTwoFactor}
          />
        </div>
      </div>
      <UserPatchDialog
        uuid={patchUserUuid ?? ''}
        open={!!patchUserUuid}
        onOpenChange={handlePatchOpenChange}
      />
      <UserArchiveDialog
        uuid={archiveUserUuid ?? ''}
        open={!!archiveUserUuid}
        onOpenChange={handleArchiveOpenChange}
      />
      <UserRecoveryDialog
        uuid={recoveryUserUuid ?? ''}
        open={!!recoveryUserUuid}
        onOpenChange={handleRecoveryOpenChange}
      />
    </SectionLayout>
  )
}
